import { Flex, Grid, GridItem, Text } from '@chakra-ui/react';
import { Accordion, ButtonText } from '@morressier/alembic';
import { useEffect, useState } from 'react';
import { ISearchQueryItems } from 'src/api/queries';
import { useReviewerSelection } from 'src/contexts/ReviewerSelectionContext';
import {
  mapSearchReviewerToInviteReviewer,
  getEmailFromReviewer,
  getReviewerPropertyValue,
  isJournalManagerPartialUser
} from 'src/utils/reviews';


import { useReviewerInviteList } from '../../../../contexts/ReviewerInviteListProvider';
import { IReviewerCOIItem } from '@publishing-engine/domain/views';
import { ILexiconField } from '@publishing-engine/domain/models';
import ReviewerInformationHeader from './ReviewerInformationHeader';
import ReviewerTagBadges from './ReviewerTagBadges';
import ReviewerPropertyRow from './ReviewerPropertyRow';
import LabelWithTooltip from './LabelWithTooltip';
import ReviewerMetricsGrid from './ReviewerMetricsGrid';
import ReviewerConflictInfo from './ReviewerConflictInfo';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowUpRightFromSquare,
  faBuildingColumns,
  faEnvelope,
  faGraduationCap
} from '@fortawesome/pro-regular-svg-icons';
import SubmittedAuthorDetailsModal from '../../AuthorsTab/AuthorProfileModal/SubmittedAuthorDetailsModal';
import { ExpandableText } from './ExpandableText';

interface Props {
  item: ISearchQueryItems;
  reviewerConfig?: ILexiconField[];
  expandAll?: boolean;
  globalLexiconConfig: boolean | undefined;
}

const useReviewerCardConfig = (reviewerConfig?: ILexiconField[]) => {
  const getConfig = (predicateLabel: string) =>
    reviewerConfig?.find(f => f.predicateLabel === predicateLabel);

  return { getConfig };
};

const getPropertyStrings = (
  properties: ISearchQueryItems['properties'],
  propertyName: string
) => properties.find(p => p.propertyName === propertyName)?.propertyValueString;

const ReviewerInformation = ({
  item,
  reviewerConfig,
  expandAll,
  globalLexiconConfig
}: Props) => {
  const { selectedReviewers, toggleReviewer } = useReviewerSelection();
 
  const { addReviewer, removeReviewersByEmails, reviewerList } =
    useReviewerInviteList();
  const [errors, setErrors] = useState<IReviewerCOIItem[]>([]);
  const [warnings, setWarnings] = useState<IReviewerCOIItem[]>([]);
  const [info, setInfo] = useState<IReviewerCOIItem[]>([]);
  const [profileUserId, setProfileUserId] = useState<string | null>(null);
  const reviewerEmail = getEmailFromReviewer(item);
  const reviewerUserId = getReviewerPropertyValue(
    item.properties,
    'ReviewerUserID'
  );
  const { getConfig } = useReviewerCardConfig(reviewerConfig);

  const userTags = getPropertyStrings(item.properties, 'userTag') ?? [];
  const emailValues = getPropertyStrings(
    item.properties,
    'ReviewerPrimaryEmailAddress'
  );
  const specialtyValues = getPropertyStrings(
    item.properties,
    'ReviewerSpecialty'
  );
  const subSpecialtyValues = getPropertyStrings(
    item.properties,
    'ReviewerSubSpecialty'
  );
  const keywordValues = getPropertyStrings(item.properties, 'ReviewerKeyword');
  const degreeValues = getPropertyStrings(item.properties, 'ReviewerDegree');
  const scandinavianValues = getPropertyStrings(
    item.properties,
    'scandinavianKeywords'
  );

  const isSelected = selectedReviewers.some(
    reviewer => getEmailFromReviewer(reviewer) === reviewerEmail
  );

  const handleCheckboxChange = () => {
    if (isSelected) {
      toggleReviewer(item);
      removeReviewersByEmails([reviewerEmail]);
      return;
    }

    toggleReviewer(item);
    const mapped = mapSearchReviewerToInviteReviewer(item);
    if (mapped) {
      addReviewer(mapped);
    }
  };

  useEffect(() => {
    setErrors(item?.error ?? []);
    setWarnings(item?.warning ?? []);
    const combinedInfo: IReviewerCOIItem[] = [...(item?.info ?? [])];
    setInfo(combinedInfo);
  }, []);

  const institutionConfig = getConfig('ReviewerInstitution');
  const specialtyConfig = getConfig('ReviewerSpecialty');
  const subSpecialtyConfig = getConfig('ReviewerSubSpecialty');
  const keywordConfig = getConfig('ReviewerKeyword');
  const scandinavianConfig = getConfig('scandinavianKeywords');
  const degreeConfig = getConfig('ReviewerDegree');

  const expertiseFields = [
    { config: specialtyConfig, values: specialtyValues },
    { config: subSpecialtyConfig, values: subSpecialtyValues },
    { config: keywordConfig, values: keywordValues },
    { config: scandinavianConfig, values: scandinavianValues }
  ].flatMap(field =>
    field.config?.reviewerCard === true
      ? [{ config: field.config, values: field.values }]
      : []
  );

  const visibleMetrics = [
    'AverageRScore',
    'ActiveReviews',
    'NumberOfTimesSubmitted',
    'AverageReviewerTurnaroundTime',
    'ReviewsInvited',
    'ReviewsNoReply',
    'ReviewsDeclined'
  ]
    .map(key => ({ key, config: getConfig(key) }))
    .filter(
      (item): item is { key: string; config: ILexiconField } =>
        item.config !== undefined && item.config.reviewerCard === true
    );

  const collapsedMetrics = [
    'TimesUninvited',
    'TimesSelected',
    'DaysSinceLastReview',
    'LastInvitedDate',
    'PercentAcceptedNoReview',
    'PercentInvitedNoReview',
    'ReviewerOnboardedDate',
    'AcceptedNoReview',
    'Agreed',
    'ReviewerPersonDesignation',
    'TotalOpenInvites',
    'LastSubmittedDate'
  ]
    .map(key => ({ key, config: getConfig(key) }))
    .filter(
      (item): item is { key: string; config: ILexiconField } =>
        item.config !== undefined && item.config.reviewerCard === true
    );

  const othersFields = [
    'ReviewerSecondaryEmailAddress',
    'ReviewerPosition',
    'ReviewerPeopleNotes',
    'ReviewerDetailedPeopleNotes',
    'ReviewerQualifications',
    'ReviewerOrigin',
    'ReviewerInterests',
    'ReviewerGenericNotes',
    'ReviewerBiography'
  ]
    .map(key => ({ key, config: getConfig(key) }))
    .filter(
      (item): item is { key: string; config: ILexiconField } =>
        item.config !== undefined && item.config.reviewerCard === true
    );

  const inviteDisabled =
    item?.error?.length > 0 ||
    errors?.length > 0 ||
    item?.isAllowed === false ||
    reviewerList?.find(r => r.email === emailValues?.[0])?.invited === true;

  return (
    <>
      {profileUserId && (
        <SubmittedAuthorDetailsModal
          userId={profileUserId}
          onBackClick={() => setProfileUserId(null)}
          backButtonText="Back to Reviewer Search"
        />
      )}
      <Accordion
        key={expandAll ? 'expanded' : 'collapsed'}
        chakraAccordionProps={{
          allowMultiple: true,
          defaultIndex: expandAll ? [0] : undefined,
          width: '100%',
          background: 'surfacePrimaryFill'
        }}
        items={[
          {
            title: (
              <>
                {' '}
                <ReviewerInformationHeader
                  item={item}
                  isSelected={isSelected}
                  onChange={handleCheckboxChange}
                  disabled={inviteDisabled}
                />
              </>
            ),
            content: (
              <>
                <Flex flexDirection="column" mb={2} gap={1}>
                  {isJournalManagerPartialUser(item) &&
                    reviewerUserId !== '-' && (
                      <Flex justifyContent="flex-end">
                        <ButtonText
                          size="regular"
                          iconLeft={
                            <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                          }
                          onClick={() => setProfileUserId(reviewerUserId)}
                        >
                          View Profile
                        </ButtonText>
                      </Flex>
                    )}
                  <ReviewerTagBadges tags={userTags} />
                  {institutionConfig &&
                    institutionConfig.reviewerCard === true && (
                      <ReviewerPropertyRow
                        config={institutionConfig}
                        icon={faBuildingColumns}
                        values={
                          [
                            getPropertyStrings(
                              item.properties,
                              'ReviewerInstitution'
                            )?.[0],
                            getPropertyStrings(
                              item.properties,
                              'ReviewerDepartment'
                            )?.[0],
                            getPropertyStrings(
                              item.properties,
                              'ReviewerCountryOrRegion'
                            )?.[0]
                          ].filter(Boolean) as string[]
                        }
                        marginLeft={'1'}
                      />
                    )}
                  <Flex
                    gap={4}
                    wrap="wrap"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Flex
                      alignItems="baseline"
                      justifyContent={'space-between'}
                      gap={1}
                    >
                      <Flex alignItems="center" gap={1}>
                        <FontAwesomeIcon icon={faEnvelope} />
                        <Text textStyle="textContext" fontWeight="600">
                          Email:
                        </Text>
                        <Text
                          textStyle="textContext"
                          paddingLeft={0}
                          whiteSpace="normal"
                          overflowWrap="anywhere"
                          wordBreak="break-word"
                        >
                          {emailValues?.length ? emailValues.join(', ') : '-'}
                        </Text>
                      </Flex>
                    </Flex>{' '}
                  </Flex>

                  {degreeConfig && degreeConfig.reviewerCard === true && (
                    <ReviewerPropertyRow
                      config={degreeConfig}
                      icon={faGraduationCap}
                      values={degreeValues}
                      marginLeft={'1'}
                    />
                  )}

                  <Grid templateColumns="repeat(1, 1fr)" gap={1}>
                    {expertiseFields.map(field => (
                      <Flex
                        textStyle="textContext"
                        key={field?.config?.predicateLabel}
                      >
                        <LabelWithTooltip config={field?.config} />:
                        <Text
                          textStyle="textContext"
                          ml={1}
                          wordBreak="break-word"
                        >
                          {field?.values?.length
                            ? field?.values?.join(', ')
                            : '-'}
                        </Text>
                      </Flex>
                    ))}
                  </Grid>
                  <Grid templateColumns="repeat(1, 1fr)" gap={0.5}>
                    {othersFields?.map(({ key, config }) => {
                      const journalValue = getReviewerPropertyValue(
                        item.properties,
                        key
                      );
                      return (
                        <GridItem
                          key={key}
                          textStyle="textContext"
                          display="flex"
                          alignItems="stretch"
                          gap={1}
                        >
                          <LabelWithTooltip config={config} /> :
                          <Flex
                            alignItems={'start'}
                            gap={2}
                            direction={key.includes('Date') ? 'column' : 'row'}
                          >
                            <Flex alignItems="center" gap={1}>
                              <ExpandableText text={journalValue} />
                            </Flex>
                          </Flex>
                        </GridItem>
                      );
                    })}
                  </Grid>
                  <ReviewerMetricsGrid
                    item={item}
                    visibleMetrics={visibleMetrics}
                    collapsedMetrics={collapsedMetrics}
                    globalLexiconConfig={globalLexiconConfig}
                  />
                  <ReviewerConflictInfo
                    errors={errors}
                    warnings={warnings}
                    info={info}
                  />
                </Flex>
              </>
            )
          }
        ]}
      />
    </>
  );
};

export default ReviewerInformation;
